import { useState, useEffect } from 'react'
import { useAppSelector, useAppDispatch } from '@state/hooks'
import { fetchExecutionData } from '@state/slices/executionSlice'
import { executionApi } from '@services/executionApi'
import { useIsSampleCompany, SAMPLE_COMPANY_TOOLTIP } from '@state/hooks/useIsSampleCompany'
import './ExecutionTab.css'

const STATUS_COLORS: Record<string, string> = {
  planned: '#6b7280',
  in_progress: '#3b82f6',
  blocked: '#ef4444',
  completed: '#10b981',
}

const HEALTH_COLORS: Record<string, string> = {
  on_track: '#10b981',
  at_risk: '#f59e0b',
  off_track: '#ef4444',
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
}

function ExecutionTab() {
  const dispatch = useAppDispatch()
  const activeCompanyId = useAppSelector(state => state.companies.activeCompanyId)
  const companyId = activeCompanyId ? parseInt(activeCompanyId.replace('company_', '')) : null

  const {
    initiatives, alerts, health, dashboard,
    escalatedBlockers, loading
  } = useAppSelector(state => state.execution)

  const [statusFilter, setStatusFilter] = useState<string>('')
  const isSampleCompany = useIsSampleCompany()

  useEffect(() => {
    if (companyId) dispatch(fetchExecutionData(companyId))
  }, [companyId, dispatch])

  const loadData = () => {
    if (companyId) dispatch(fetchExecutionData(companyId))
  }

  const handleStatusChange = async (id: number, newStatus: string) => {
    if (!companyId) return
    if (isSampleCompany) return
    await executionApi.updateInitiative(companyId, id, { status: newStatus } as any)
    loadData()
  }

  const handleScan = async () => {
    if (!companyId) return
    if (isSampleCompany) return
    await executionApi.triggerScan(companyId)
    loadData()
  }

  if (!companyId) {
    return <div className="execution-tab empty">Select a company to view execution tracking.</div>
  }

  if (loading) {
    return <div className="execution-tab loading">Loading execution data...</div>
  }

  const healthCounts = {
    on_track: health.filter(h => h.health === 'on_track').length,
    at_risk: health.filter(h => h.health === 'at_risk').length,
    off_track: health.filter(h => h.health === 'off_track').length,
  }

  return (
    <div className="execution-tab">
      <div className="execution-main">
        {/* Health Summary */}
        <div className="health-summary">
          <h4>Execution Health</h4>
          <div className="health-cards">
            {Object.entries(healthCounts).map(([key, count]) => (
              <div key={key} className="health-card" style={{ borderColor: HEALTH_COLORS[key] }}>
                <span className="health-count">{count}</span>
                <span className="health-label">{key.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
          {dashboard && (
            <div className="dashboard-stats">
              <span>Total: {dashboard.total}</span>
              <span>Completion: {dashboard.completion_rate}%</span>
            </div>
          )}
        </div>

        {/* Blocker Escalation Panel */}
        {escalatedBlockers.length > 0 && (
          <div className="blocker-escalation">
            <h5>Escalated Blockers ({escalatedBlockers.length})</h5>
            <p className="escalation-note">Initiatives blocked for 7+ days requiring attention:</p>
            {escalatedBlockers.map(b => (
              <div key={b.id} className="blocker-item">
                <span className="blocker-title">{b.title}</span>
                <span className="blocker-days">{b.days_blocked} days</span>
                {b.blockers && b.blockers.length > 0 && (
                  <div className="blocker-reasons">
                    {b.blockers.map((reason: string, i: number) => (
                      <span key={i} className="blocker-reason">{reason}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Status Filter */}
        <div className="filter-bar">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); }}>
            <option value="">All Statuses</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In Progress</option>
            <option value="blocked">Blocked</option>
            <option value="completed">Completed</option>
          </select>
          <button className="btn-secondary btn-sm" onClick={loadData}>Refresh</button>
        </div>

        {/* Initiative List */}
        {initiatives.length === 0 ? (
          <div className="empty-state">
            <p>No initiatives tracked yet.</p>
            <p className="hint">Complete a strategy session and import initiatives to begin tracking.</p>
          </div>
        ) : (
          <div className="initiative-list">
            {initiatives.map(init => (
              <div key={init.id} className="initiative-card">
                <div className="initiative-header">
                  <span className="initiative-code">{init.initiative_code}</span>
                  <span className="initiative-title">{init.title}</span>
                  <select
                    className="status-select"
                    value={init.status}
                    onChange={(e) => handleStatusChange(init.id, e.target.value)}
                    disabled={isSampleCompany}
                    title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : undefined}
                    style={{ borderColor: STATUS_COLORS[init.status] || '#6b7280' }}
                  >
                    <option value="planned">Planned</option>
                    <option value="in_progress">In Progress</option>
                    <option value="blocked">Blocked</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                {init.description && (
                  <p className="initiative-desc">{init.description.substring(0, 150)}</p>
                )}
                <div className="initiative-meta">
                  {init.pillar_name && <span className="meta-tag">{init.pillar_name}</span>}
                  {init.bsc_perspective && <span className="meta-tag">{init.bsc_perspective}</span>}
                  {init.owner && <span className="meta-tag">Owner: {init.owner}</span>}
                  {init.target_date && <span className="meta-tag">Due: {init.target_date}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Alert Sidebar */}
      <div className="alert-sidebar">
        <div className="alert-header">
          <h4>Alerts</h4>
          <div className="alert-scan-center">
            <button
              className="btn-primary btn-sm"
              onClick={handleScan}
              disabled={isSampleCompany}
              title={isSampleCompany ? SAMPLE_COMPANY_TOOLTIP : undefined}
            >
              Scan
            </button>
          </div>
        </div>
        {alerts.length === 0 ? (
          <p className="no-alerts">No active alerts</p>
        ) : (
          <div className="alert-feed">
            {alerts.map(alert => (
              <div key={alert.id} className="alert-card" style={{ borderLeftColor: SEVERITY_COLORS[alert.severity] }}>
                <div className="alert-severity" style={{ color: SEVERITY_COLORS[alert.severity] }}>
                  {alert.severity.toUpperCase()}
                </div>
                <div className="alert-title">{alert.title}</div>
                {alert.description && <div className="alert-desc">{alert.description}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default ExecutionTab
